export const getSuggestionText = (suggestion) => {
  if (typeof suggestion === 'string') return suggestion.trim()
  if (typeof suggestion?.name === 'string') return suggestion.name.trim()
  return ''
}

export const getSuggestionKey = (accordionId, suggestion) => `${accordionId}-${getSuggestionText(suggestion).toLowerCase()}`

export const getTitleKey = (accordionId) => `title-${accordionId}`

const normalizeSuggestionText = (value = '') => String(value).trim().toLowerCase()

export const getSuggestionOrderData = (accordion, suggestion, index) => {
  const orders = Array.isArray(accordion?.suggestions?.orders) ? accordion.suggestions.orders : []
  const suggestionName = normalizeSuggestionText(getSuggestionText(suggestion))

  if (!orders.length || !suggestionName) return null

  const matchingOrder = orders.find((order) => normalizeSuggestionText(getSuggestionText(order)) === suggestionName)
  if (matchingOrder) return matchingOrder?.data || null

  const indexOrder = orders[index]
  const hasNamedOrders = orders.some((order) => getSuggestionText(order))

  if (!hasNamedOrders || !getSuggestionText(indexOrder)) {
    return indexOrder?.data || null
  }

  return null
}

export const getHeaderOrderData = (accordion) => {
  const orders = Array.isArray(accordion?.headerPlanSuggestions?.orders) ? accordion.headerPlanSuggestions.orders : []
  const titleName = normalizeSuggestionText(accordion?.title)

  if (!orders.length) return null

  const matchingOrder = orders.find((order) => normalizeSuggestionText(getSuggestionText(order)) === titleName)
  if (matchingOrder) return matchingOrder?.data || null

  if (orders.length === 1) return orders[0]?.data || null

  return null
}

export const collectSelectedAssessmentOrderData = (accordions, selectedKeys) => {
  const selectedKeySet = new Set(Array.isArray(selectedKeys) ? selectedKeys : [])
  const orders = []

  const visit = (items, parentSelected = false) => {
    for (const accordion of Array.isArray(items) ? items : []) {
      const titleSelected = selectedKeySet.has(getTitleKey(accordion?.id))
      const headerOrderData = titleSelected ? getHeaderOrderData(accordion) : null
      const includeAllOrders = parentSelected || (titleSelected && !headerOrderData)
      const suggestions = Array.isArray(accordion?.suggestions?.suggestionName) ? accordion.suggestions.suggestionName : []

      if (headerOrderData) {
        orders.push(headerOrderData)
      }

      suggestions.forEach((suggestion, index) => {
        if (!includeAllOrders && !selectedKeySet.has(getSuggestionKey(accordion?.id, suggestion))) return

        const data = getSuggestionOrderData(accordion, suggestion, index)
        if (data) orders.push(data)
      })

      visit(accordion?.children, includeAllOrders)
    }
  }

  visit(accordions)
  return orders
}

export const extractPlanSuggestionItems = (accordions) => {
  const collected = []

  const visit = (items) => {
    for (const accordion of Array.isArray(items) ? items : []) {
      for (const suggestion of Array.isArray(accordion?.suggestions?.suggestionName) ? accordion.suggestions.suggestionName : []) {
        const name = getSuggestionText(suggestion)
        if (!name) continue

        collected.push(typeof suggestion === 'string' ? { name } : { ...suggestion, name })
      }

      visit(accordion?.children)
    }
  }

  visit(accordions)
  return collected
}

export const buildHeaderPlanSelection = (accordion, titleLabel) => {
  const key = getTitleKey(accordion?.id)
  const suggestions = extractPlanSuggestionItems(accordion?.headerPlanSuggestions?.accordions)

  if (!suggestions.length) return null

  return {
    id: accordion?.id,
    key,
    name: titleLabel || accordion?.title || '',
    suggestions,
    description: accordion?.description || '',
  }
}

export const buildStatementPlanSelection = (accordion, suggestion) => {
  const name = getSuggestionText(suggestion)
  if (!name) return null

  const matchingPlan = (Array.isArray(accordion?.suggestions?.planSuggestions) ? accordion.suggestions.planSuggestions : []).find((plan) => getSuggestionText(plan) === name)
  const suggestions = extractPlanSuggestionItems(matchingPlan?.accordions)

  if (!suggestions.length) return null

  return {
    id: accordion?.id,
    key: getSuggestionKey(accordion?.id, suggestion),
    name,
    suggestions,
    description: typeof suggestion === 'object' && suggestion !== null ? suggestion.description || '' : '',
  }
}

export const restoreAssessmentPlanSelections = (accordions, storedKeys, getTitleLabel = (accordion) => accordion?.title || '') => {
  const selectedKeySet = new Set(Array.isArray(storedKeys) ? storedKeys : [])
  const assessmentKeys = []
  const planSources = []

  const visit = (items) => {
    for (const accordion of Array.isArray(items) ? items : []) {
      const titleKey = getTitleKey(accordion?.id)

      if (selectedKeySet.has(titleKey)) {
        assessmentKeys.push(titleKey)
        const headerPlan = buildHeaderPlanSelection(accordion, getTitleLabel(accordion))
        if (headerPlan) planSources.push(headerPlan)
      }

      const suggestions = Array.isArray(accordion?.suggestions?.suggestionName) ? accordion.suggestions.suggestionName : []

      for (const suggestion of suggestions) {
        const suggestionKey = getSuggestionKey(accordion?.id, suggestion)
        if (!selectedKeySet.has(suggestionKey)) continue

        assessmentKeys.push(suggestionKey)
        const statementPlan = buildStatementPlanSelection(accordion, suggestion)
        if (statementPlan) planSources.push(statementPlan)
      }

      visit(accordion?.children)
    }
  }

  visit(accordions)

  return {
    assessmentKeys: [...new Set(assessmentKeys)],
    planSources: [...new Map(planSources.map((item) => [item.key, item])).values()],
  }
}

export const escapeSuggestionHtml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')

export const renderAssessmentSuggestionHtml = (text = '') => {
  if (!text) return ''

  return String(text)
    .replace(/\r\n/g, '\n')
    .split('\n')
    .filter((line) => line.trim() !== '')
    .map((line) => {
      const trimmedLine = line.trim()
      const indentLevel = Math.max(0, Math.floor((line.length - line.trimStart().length) / 2))
      const marginLeft = `${indentLevel * 20}px`

      if (trimmedLine.startsWith('- ')) {
        const statementText = trimmedLine.slice(2)
        return `<div style="margin-left:${marginLeft}; line-height:1.6; padding-left:18px; position:relative;"><span style="position:absolute; left:0;">&bull;</span>${escapeSuggestionHtml(statementText)}</div>`
      }

      const fontWeight = indentLevel === 0 ? '700' : '400'
      return `<div style="margin-left:${marginLeft}; line-height:1.6; font-weight:${fontWeight};">${escapeSuggestionHtml(trimmedLine)}</div>`
    })
    .join('')
}

export const renderPlanSuggestionsHtml = (text = '') =>
  String(text)
    .replace(/\r\n/g, '\n')
    .split('\n')
    .filter((line) => line.trim())
    .map((line) => {
      const leadingWhitespace = line.match(/^[\t ]*/)?.[0] || ''
      const indentLevel = (leadingWhitespace.match(/\t/g)?.length || 0) + Math.floor((leadingWhitespace.match(/ /g)?.length || 0) / 2)
      const content = line.trim().replace(/^[•\-*]\s*/, '').trim()

      if (!content) return ''

      if (indentLevel === 0) {
        return `<div style="margin:0 0 3px 0;font-weight:700;line-height:1.4;">${escapeSuggestionHtml(content)}</div>`
      }

      return `<div style="margin-left:${indentLevel * 20}px;padding-left:16px;position:relative;line-height:1.4;"><span style="position:absolute;left:0;">&bull;</span>${escapeSuggestionHtml(content)}</div>`
    })
    .join('')
